import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaDoorOpen, FaRedoAlt, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getExternalMaintenanceTasks, maintenanceExternalError } from './maintenanceExternalApi';
import { clearMaintenanceSession, getMaintenanceSession, selectMaintenanceCentre } from './maintenanceExternalSession';
import { MaintenanceErrorState, MaintenanceLinearLoading, MaintenancePageShell } from './MaintenancePageShell';
import MaintenanceTaskCard from './MaintenanceTaskCard';

const statusFilters = [
  { value: 'open,in progress,pending', label: 'Active' },
  { value: 'open', label: 'Open' },
  { value: 'in progress', label: 'In progress' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' }
];

export default function MaintenanceTaskListPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => getMaintenanceSession());
  const [centreId, setCentreId] = useState(() => Number(getMaintenanceSession()?.selected_centre_id || 0));
  const [statusFilter, setStatusFilter] = useState(statusFilters[0].value);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const sequence = useRef(0);

  const selectedCentre = session?.centres?.find(centre => Number(centre.id) === Number(centreId));

  const load = useCallback(() => {
    if (!session?.token || !centreId) return () => {};
    const request = ++sequence.current;
    const controller = new AbortController();
    if (result) setRefreshing(true); else setLoading(true);
    setError('');
    getExternalMaintenanceTasks(session.token, {
      centre_id: centreId,
      status: statusFilter,
      search,
      page,
      page_size: 20,
      completed_recent: statusFilter === 'completed' ? 1 : undefined
    }, controller.signal)
      .then(data => {
        if (request !== sequence.current) return;
        setResult(data);
        if (Number(data.page) !== page) setPage(Number(data.page));
      })
      .catch(reason => {
        if (request !== sequence.current || reason?.code === 'ERR_CANCELED') return;
        if (reason?.response?.status === 401) clearMaintenanceSession();
        setError(maintenanceExternalError(reason, 'Your maintenance tasks could not be loaded.'));
      })
      .finally(() => {
        if (request !== sequence.current) return;
        setLoading(false);
        setRefreshing(false);
      });
    return () => controller.abort();
  }, [session?.token, centreId, statusFilter, search, page, reloadKey]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    const onFocus = () => setReloadKey(value => value + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (!session) {
    return (
      <MaintenancePageShell>
        <MaintenanceErrorState
          title="Secure link required"
          message="Open the Maintenance link sent by your manager through WhatsApp."
        />
      </MaintenancePageShell>
    );
  }

  const changeCentre = event => {
    const nextCentre = Number(event.target.value);
    const nextSession = selectMaintenanceCentre(nextCentre);
    if (!nextSession) return;
    setSession(nextSession);
    setCentreId(nextCentre);
    setPage(1);
  };

  const submitSearch = event => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const endSession = () => {
    clearMaintenanceSession();
    setSession(null);
  };

  const headerAction = (
    <button type="button" className="maintenance-icon-button" onClick={() => setReloadKey(value => value + 1)} aria-label="Refresh tasks" disabled={refreshing}>
      <FaRedoAlt className={refreshing ? 'is-spinning' : ''} aria-hidden="true" />
    </button>
  );

  const summary = result?.summary || {};
  const items = result?.items || [];

  return (
    <MaintenancePageShell staffName={session.staff?.name} centreName={selectedCentre?.name} action={headerAction}>
      <section className="maintenance-list-hero">
        <div>
          <span className="maintenance-eyebrow">Maintenance work</span>
          <h1>Tasks to follow up</h1>
        </div>
        <button type="button" className="maintenance-text-button" onClick={endSession}><FaDoorOpen aria-hidden="true" /> End session</button>
      </section>

      <section className="maintenance-filter-card" aria-label="Maintenance task filters">
        <label htmlFor="maintenance-centre">Centre</label>
        <select id="maintenance-centre" value={centreId} onChange={changeCentre}>
          {session.centres.map(centre => <option key={centre.id} value={centre.id}>{centre.name}</option>)}
        </select>

        <div className="maintenance-summary-grid" aria-label="Selected centre task summary">
          <div><span>All tasks</span><strong>{Number(summary.total ?? summary.assigned ?? 0)}</strong></div>
          <div><span>In progress</span><strong>{Number(summary.in_progress || 0)}</strong></div>
          <div><span>Pending</span><strong>{Number(summary.pending || 0)}</strong></div>
          <div className={Number(summary.critical || 0) ? 'is-critical' : ''}><span>Critical</span><strong>{Number(summary.critical || 0)}</strong></div>
        </div>

        <div className="maintenance-filter-chips" aria-label="Filter by status">
          {statusFilters.map(filter => (
            <button
              type="button"
              key={filter.value}
              className={statusFilter === filter.value ? 'is-selected' : ''}
              aria-pressed={statusFilter === filter.value}
              onClick={() => { setStatusFilter(filter.value); setPage(1); }}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <form className="maintenance-search" onSubmit={submitSearch}>
          <label htmlFor="maintenance-search">Search tasks</label>
          <div>
            <FaSearch aria-hidden="true" />
            <input
              id="maintenance-search"
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              placeholder="Issue number, title or category"
            />
            <button type="submit">Search</button>
          </div>
        </form>
      </section>

      {loading ? (
        <MaintenanceLinearLoading />
      ) : error ? (
        <MaintenanceErrorState message={error} onRetry={() => setReloadKey(value => value + 1)} />
      ) : (
        <section className={`maintenance-task-list ${refreshing ? 'is-refreshing' : ''}`} aria-busy={refreshing}>
          <div className="maintenance-task-list__heading">
            <div><span className="maintenance-eyebrow">Task directory</span><h2>{selectedCentre?.name || 'Selected centre'}</h2></div>
            <span>{result?.total || 0} task{Number(result?.total) === 1 ? '' : 's'}</span>
          </div>
          {items.length ? (
            <div className="maintenance-task-list__items">
              {items.map(task => <MaintenanceTaskCard key={task.id} task={task} onOpen={id => navigate(`/maintenance/tasks/${id}`)} />)}
            </div>
          ) : (
            <div className="maintenance-empty-state">
              <h3>No tasks found</h3>
              <p>There are no tasks for this centre and filter.</p>
            </div>
          )}
          {Number(result?.total_pages || 1) > 1 && (
            <nav className="maintenance-pagination" aria-label="Task pages">
              <button type="button" onClick={() => setPage(value => Math.max(1, value - 1))} disabled={page <= 1}>Previous</button>
              <span>Page {result.page} of {result.total_pages}</span>
              <button type="button" onClick={() => setPage(value => Math.min(Number(result.total_pages), value + 1))} disabled={page >= Number(result.total_pages)}>Next</button>
            </nav>
          )}
        </section>
      )}
    </MaintenancePageShell>
  );
}
